import {Component, computed, effect, signal, Signal} from '@angular/core';
import {ActivatedRoute, RouterModule} from '@angular/router';
import {combineLatest, Observable, of, switchMap} from 'rxjs';
import {Apollo} from 'apollo-angular';
import {Airline, Query} from '../../types-workflow';
import {catchError, debounceTime, distinctUntilChanged, map, startWith} from 'rxjs/operators';
import {errorText} from '../../graphql/error-link';
import {LoadRowComponent} from '../../graphql/load-row.component';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {FILTER_DEBOUNCE_TIME, FilterSectionComponent} from '../filter-section/filter-section.component';
import {BreadCrumb, LogoAreaComponent} from '../logo-area/logo-area.component';
import {Organization} from '../../types-repository';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {CommonModule} from '@angular/common';
import {OrgResolverPipe} from '../../pipes/org-resolver-pipe';
import {ContextService} from '../../services/context.service';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {LIST_POS} from '../../queries-workflow';
import {clpp} from '@castlabs/prestoplay';
import {DateComponent} from '../../date/date.component';
import {Title} from '@angular/platform-browser';


class UIPo {
  airline: string;
  po_name: string;
  date_created?: string;
  finalized: boolean;
  /** null when the API could not provide the publish summary. */
  published: number | null;
  total: number | null;
  target_system?: string;
}

function sumOrNull(a: number | null, b: number | null): number | null {
  return a === null || b === null ? null : a + b;
}

export type PublishState = 'full' | 'partial' | 'none';

/**
 * The Finalized column: a check only once the PO is finalized AND every item is published; a
 * distinct "partial" state while some items are published but not all, whether or not the PO was
 * finalized. Without a publish summary it can only go by the finalize process, as it used to.
 */
export function publishState(po: {finalized: boolean, published: number | null, total: number | null}): PublishState {
  if (po.published === null || po.total === null) {
    return po.finalized ? 'full' : 'none';
  }
  if (po.finalized && po.published >= po.total) {
    return 'full';
  }
  // Partial means items are missing: some published but not all, or finalized with any missing. A
  // PO with every item published but not finalized is not partial; the column is "Finalized".
  if (po.published < po.total && (po.finalized || po.published > 0)) {
    return 'partial';
  }
  return 'none';
}


@Component({
  selector: 'app-list-pos',
  templateUrl: './list-pos.component.html',
  styleUrls: ['./list-pos.component.css'],
  imports: [
    LoadRowComponent,
    HeadlineSectionComponent,
    LogoAreaComponent,
    FilterSectionComponent,
    ReactiveFormsModule,
    RouterModule,
    OrgResolverPipe,
    CommonModule,
    DateComponent
  ]
})
export class ListPosComponent {
  readonly publishState = publishState;
  poprefix$: Observable<string>;

  organization: Signal<Organization>;
  airlines: Signal<Airline[]>;
  airline: Signal<Airline>;


  pos$: Signal<UIPo[]>;
  allpos$: Signal<UIPo[]>;
  loading = signal(true);
  loadError = signal<string | null>(null);
  loadState = computed(() => ({loading: this.loading(), data: null, error: this.loadError()}));
  breadCrumbs: Signal<BreadCrumb[]>;
  targetSystems$: Signal<string[]>;

  readonly currentYear = new Date().getFullYear();
  readonly years: number[] = Array.from({length: this.currentYear - 2019}, (_, i) => this.currentYear - i);

  filters: FormGroup;

  constructor(
    route: ActivatedRoute,
    private apollo: Apollo,
    contextService: ContextService,
    fb: FormBuilder,
    private title: Title,
    ) {
    this.filters = fb.group({
      t: new FormControl(''),
      targetSystem: new FormControl(''),
      year: new FormControl(String(this.currentYear)),
    })
    this.organization = contextService.organization
    this.airline = contextService.airline
    this.airlines = contextService.airlines
    // With a single airline the picker at /workflows is skipped entirely, so a 'Workflows' crumb
    // would point back at this very page -- drop it and leave only the path that really is above.
    this.breadCrumbs = computed(() => [
      {
        display: 'Content Platform',
        commands: ['/o', this.organization()?.id],
      },
      ...(contextService.soleAirline() ? [] : [{
        display: 'Workflows',
        commands: ['/o', this.organization()?.id, 'workflows'],
      }]),
    ]);
    this.poprefix$ = route.params.pipe(map(p => p['poprefix'] || ''));

    const year$ = this.filters.get('year').valueChanges.pipe(startWith(String(this.currentYear)), distinctUntilChanged());

    this.allpos$ = toSignal(combineLatest([toObservable(this.airline), toObservable(this.organization), this.poprefix$, year$]).pipe(
      switchMap(([airline, organization, poprefix, year]) => {
        if (!airline || !organization) {
          this.loading.set(false)
          return of([]); // Emit an empty array if either is missing
        }
        this.loading.set(true);
        this.loadError.set(null);
        const variables: { airline: string; date_created?: { between: [string, string] } } = {
          airline: airline.iata_code,
        };
        if (year) {
          variables.date_created = { between: [`${year}-01-01T00:00:00.000Z`, `${year}-12-31T23:59:59.999Z`] };
        }
        return this.apollo.use('workflow').query<Query>({
          query: LIST_POS(organization.id),
          fetchPolicy: 'no-cache',
          variables,
        }).pipe(
          map(d => {
            if (!d.data) {
              return [];
            }
            const pos: UIPo[] = [];
            console.log(poprefix)
            for (const po of d.data.list_POs.pos) {
              if (!poprefix || po.po_name.startsWith(poprefix)) {
                pos.push({
                  airline: po.airline,
                  po_name: po.po_name,
                  date_created: po.date_created,
                  finalized: po.finalize_process?.state === 'SUCCESS',
                  published: po.publish_summary?.published ?? null,
                  total: po.publish_summary?.total ?? null,
                  target_system: po.target_system
                });
              }
            }
            return pos;
          }),
          map(pos => {
            const uiPos: UIPo[] = [];
            for (const po of pos) {
              if (po.po_name.includes('/') && !poprefix) {
                const [prefix] = po.po_name.split('/');
                const existingPo = uiPos.find(_po => _po.po_name === `${prefix}/`);

                if (existingPo) {
                  // A grouped row counts every PO under the prefix, not only those adding a new target system.
                  existingPo.finalized &&= po.finalized;
                  existingPo.published = sumOrNull(existingPo.published, po.published);
                  existingPo.total = sumOrNull(existingPo.total, po.total);
                  const targetSystems = existingPo.target_system ? existingPo.target_system.split(',') : [];
                  if (!targetSystems.includes(po.target_system)) {
                    targetSystems.push(po.target_system);
                    existingPo.target_system = targetSystems.join(',');
                  }
                } else {
                  uiPos.push({
                    airline: po.airline,
                    po_name: `${prefix}/`,
                    finalized: po.finalized,
                    published: po.published,
                    total: po.total,
                    date_created: po.date_created,
                    target_system: po.target_system
                  });
                }
              } else {
                const uiPosItem: UIPo = {
                  airline: po.airline,
                  po_name: po.po_name,
                  finalized: po.finalized,
                  published: po.published,
                  total: po.total,
                  date_created: po.date_created,
                  target_system: po.target_system
                };
                uiPos.push(uiPosItem);

              }
            }

            uiPos.sort((a, b) => (a.date_created < b.date_created ? 1 : a.date_created > b.date_created ? -1 : 0));
            this.loading.set(false);
            return uiPos;
          }),
          // A failed load ends in the error row, not an endless "Loading ..."; the next airline,
          // year or prefix loads again.
          catchError(error => {
            this.loading.set(false);
            this.loadError.set(errorText(error));
            return of([] as UIPo[]);
          }),
        )
      })
    ))


    // Extract unique target systems from all POs
    this.targetSystems$ = computed(() => {
      const allpos = this.allpos$();
      if (!allpos) {
        return [];
      }

      const systemsSet = new Set<string>();
      allpos.forEach(po => {
        if (po.target_system) {
          // Split comma-separated target systems and add each to the set
          po.target_system.split(',').forEach(system => {
            const trimmed = system.trim();
            if (trimmed) {
              systemsSet.add(trimmed);
            }
          });
        }
      });

      return Array.from(systemsSet).sort();
    });

    const filterSignal = toSignal(
      this.filters.valueChanges.pipe(debounceTime(FILTER_DEBOUNCE_TIME), startWith(this.filters.value))
    );
    this.pos$ = computed(() => {
        const allpos = this.allpos$();
        if (allpos) {
          return this.allpos$().filter(po => {
            const textFilter = filterSignal()['t'] || '';
            const targetSystemFilter = filterSignal()['targetSystem'] || '';

            // Text search filter (searches in both po_name and target_system)
            const matchesTextFilter = !textFilter ||
              po.po_name.toLowerCase().includes(textFilter.toLowerCase()) ||
              (po.target_system && po.target_system.toLowerCase().includes(textFilter.toLowerCase()));

            // Target system filter (checks if selected system is in the comma-separated list)
            let matchesTargetSystemFilter = true;
            if (targetSystemFilter) {
              // Filter for POs that include the selected system
              matchesTargetSystemFilter = po.target_system &&
                po.target_system.split(',').map(s => s.trim()).includes(targetSystemFilter);
            }

            return matchesTextFilter && matchesTargetSystemFilter;
          })
        } else {
          return []
        }
      }
    )
  }

  readonly pageTitleEffect = effect(() => {
  // No airline when the airlines could not be loaded (ContextService falls back to none).
  const airline = this.airline()?.airline_possessive;

  if (airline) {
    this.title.setTitle(`${airline} workflows`);
  } else {
    this.title.setTitle('Content Platform');
  }
});


}
